<template>
  <div>
    <div class="announcement section">
      <p>{{ $t("unsubscriptionAnnounce.title") }}</p>
      <div class="description">
        <div class="margin-bottom">
          {{ $t("unsubscriptionAnnounce.description.thankYou") }}
        </div>
        <div>
          {{ $t("unsubscriptionAnnounce.description.confirmation") }}
          <b>{{ $t("unsubscriptionAnnounce.description.confirmationBold") }}</b
          >{{ $t("unsubscriptionAnnounce.description.confirmationSuffix") }}
        </div>
      </div>
    </div>
    <div class="precautions section">
      <table class="primary">
        <thead>
          <tr>
            <th class="title">{{ $t("unsubscriptionAnnounce.precautions.title") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <table class="secondary">
                <tbody>
                  <tr>
                    <td>
                      <div class="number">1</div>
                    </td>
                    <td>
                      <div class="sub-title">{{ $t("unsubscriptionAnnounce.precautions.item1.subtitle") }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="message">
                <div>
                  {{ $t("unsubscriptionAnnounce.precautions.item1.message1") }}
                </div>
                <div>
                  {{ $t("unsubscriptionAnnounce.precautions.item1.message2") }}
                </div>
              </div>
            </td>
          </tr>
          <tr class="horizontal-rule">
            <td>
              <hr />
            </td>
          </tr>
          <tr>
            <td>
              <table class="secondary">
                <tbody>
                  <tr>
                    <td>
                      <div class="number">2</div>
                    </td>
                    <td>
                      <div class="sub-title">{{ $t("unsubscriptionAnnounce.precautions.item2.subtitle") }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="message">
                <div>
                  {{ $t("unsubscriptionAnnounce.precautions.item2.message1") }}
                </div>
                <div>{{ $t("unsubscriptionAnnounce.precautions.item2.message2") }}</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="operation section">
      <span class="disclaimer">
        <span>{{ $t("unsubscriptionAnnounce.operation.disclaimerPrefix") }}</span>
        <router-link class="membership" :to="{ name: 'membership' }" target="_blank">{{
          $t("unsubscriptionAnnounce.operation.membershipTerms")
        }}</router-link>
        <span>{{ $t("unsubscriptionAnnounce.operation.disclaimerSuffix") }}</span>
      </span>
      <button type="submit" class="button-design submit margin-bottom" v-on:click="onSubmit">
        {{ $t("unsubscriptionAnnounce.operation.submitButton") }}
      </button>
      <router-link class="button-design cancel" :to="{ name: 'paymentManagement' }">{{
        $t("common.cancel")
      }}</router-link>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { BillingClient } from "@/lib/clients";
import { CreatePortalSessionInput } from "@/lib/models/billing";

const billingClient = new BillingClient();

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  created() {
    window.scrollTo(0, 0);
  },
  methods: {
    async onSubmit() {
      const payload: CreatePortalSessionInput = {
        returnUrl: `${window.location.origin}/payment-management`,
      };
      const session = await billingClient.createPortalSession(payload);

      window.location.href = session.url;
    },
  },
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "@/style-common.scss";
.margin-bottom {
  margin-bottom: 15px;
}

.button-design {
  padding: 15px 50px;
  border-radius: var(--radius-rounded);
  font-weight: var(--font-weight-bold);
  border-style: none;
  color: var(--color-text-on-fixed-bg);
  text-decoration: none;
  cursor: pointer;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;

  &.submit {
    background-color: var(--color-warning);
    width: 164px;
    height: 49px;
    padding: 10px 15px;
    font-size: var(--font-size-sm);
    white-space: normal;
    line-height: 1.3;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  &.cancel {
    background-color: var(--color-primary);
    margin-bottom: 30px; // for Safari
    width: 164px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 50px 30px;
  color: var(--color-text-normal);
}

.announcement {
  p {
    font-size: 1.5em;
    font-weight: var(--font-weight-bold);
    margin-top: 0;
  }

  .description {
    max-width: 600px;
  }
}

.precautions {
  margin: 0;
  padding: 50px 30px;
  background-color: var(--color-surface-subtle);

  table.primary {
    max-width: 600px;
    border-collapse: separate;
    border-spacing: 0;
    border: 1.5px solid var(--color-border);
    border-radius: var(--radius-lg);
    background-color: var(--color-background-normal);

    th {
      border: 1.5px solid var(--color-border);
      border-top-style: none;
      border-left-style: none;
      border-right-style: none;
    }

    th,
    td {
      padding: 20px;
    }

    .title {
      font-size: 1.5em;
      font-weight: var(--font-weight-bold);
    }

    .message {
      margin: 10px;
    }

    tr.horizontal-rule {
      td {
        padding: 0 20px;

        hr {
          margin: 0;
        }
      }
    }

    table.secondary {
      border: none;

      .number {
        font-size: 2.5em;
        width: 1.5em;
        height: 1.5em;
        line-height: 1.5em;
        text-align: center;
        background-color: var(--color-surface-muted);
        border-radius: var(--radius-circle);
      }

      .sub-title {
        margin-left: 5px;
        font-size: 1.2em;
      }

      td {
        padding: 0;
        font-weight: var(--font-weight-bold);
      }
    }
  }
}

.operation {
  .disclaimer {
    max-width: 600px;
    margin-bottom: 20px;

    .membership {
      font-weight: var(--font-weight-bold);
      text-decoration: none;
      color: var(--color-primary);
    }
  }
}
</style>
